import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Bell, BellRing, BookOpen, X } from "lucide-react";
import { toast } from "sonner";
import { PCATS, addWait, campPrice, catalogUrl, fetchCatalogPages, fetchWaits, imgUrl, logo, packSize, ron } from "@/lib/bf";

const thumb = (src) => (src?.includes("cdn.shopify.com") ? `${src}${src.includes("?") ? "&" : "?"}width=160` : imgUrl(src));

const PAGE = 20;

export const sheetRows = (c, byId) => c.items.map((id) => byId[id]).filter((p) => p && p.salon > 0).flatMap((p) => {
  const base = { cat: p.cat || "", image: p.image || "", brand: p.brand, pid: p.id, pout: !!p.out, size: packSize(p), price: campPrice(p, c), list: Number(p.salon) };
  if (!p.shades?.length) return [{ ...base, key: `cp:${c.id}:${p.id}`, code: p.code || "", name: p.name, out: !!p.out, hay: `${p.code || ""} ${p.name} ${p.desc || ""}` }];
  return p.shades.map((s) => ({ ...base, key: `cp:${c.id}:${p.id}:${s.code}`, code: s.code, name: `${p.name} · nuanța ${s.code}${s.name ? ` ${s.name}` : ""}`, out: s.out || !!p.out, hay: `${p.code || ""} ${s.code} ${s.name} ${p.name}` }));
});

const Notify = ({ r, wait }) => (wait.has(r.pid)
  ? <em className="bf-sheet-waiting" data-testid={`bf-sheet-waiting-${r.key}`}><BellRing size={12} /> Te anunțăm</em>
  : <button type="button" className="bf-sheet-notify" onClick={() => wait.add(r.pid)} data-testid={`bf-sheet-notify-${r.key}`}><Bell size={12} /> Anunță-mă</button>);

const Row = ({ r, q, setQty, onZoom, wait, hasPage }) => (
  <tr className={`${q ? "is-on" : ""} ${r.out ? "is-out" : ""}`} data-testid={`bf-sheet-row-${r.key}`}>
    <td className="bf-sheet-img">
      <button type="button" className={r.image || hasPage ? "" : "is-empty"} onClick={() => (r.image || hasPage) && onZoom(r)} aria-label={`Vezi poza ${r.name}`} data-testid={`bf-sheet-img-${r.key}`}>
        <img src={r.image ? thumb(r.image) : logo(r.brand)} alt="" loading="lazy" />
      </button>
    </td>
    <td className="bf-sheet-code">{r.code || "–"}</td>
    <td className="bf-sheet-name">
      <span>{r.name}</span>
      <small className="bf-sheet-meta">{[r.code, r.size].filter(Boolean).join(" · ")}</small>
    </td>
    <td className="bf-sheet-size">{r.size || "–"}</td>
    <td className="bf-sheet-price"><b>{ron(r.price)}</b><s>{ron(r.list)}</s></td>
    <td className="bf-sheet-qty">
      {r.out ? (r.pout ? <><em>stoc epuizat</em><Notify r={r} wait={wait} /></> : <em>stoc epuizat</em>) : (
        <input type="number" inputMode="numeric" min="0" max="999" value={q || ""} placeholder="0"
          onChange={(e) => setQty(r.key, parseInt(e.target.value, 10) || 0)} aria-label={`Cantitate ${r.name}`} data-testid={`bf-sheet-qty-${r.key}`} />
      )}
    </td>
    <td className="bf-sheet-total" data-testid={`bf-sheet-total-${r.key}`}>{q ? ron(r.price * q) : "–"}</td>
  </tr>
);

const Pager = ({ page, pages, setPage, id }) => pages > 1 && (
  <div className="bf-sheet-pager" data-testid={`bf-sheet-pager-${id}`}>
    <button type="button" disabled={!page} onClick={() => setPage(page - 1)} data-testid={`bf-sheet-prev-${id}`}><ChevronLeft size={15} /> Înapoi</button>
    <span data-testid={`bf-sheet-page-${id}`}>Pagina {page + 1} din {pages}</span>
    <button type="button" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} data-testid={`bf-sheet-next-${id}`}>Dă pagina <ChevronRight size={15} /></button>
  </div>
);

const Section = ({ id, title, rows, cart, setQty, q, onZoom, wait, catalogs, pages }) => {
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [q]);
  const pageCount = Math.ceil(rows.length / PAGE);
  const sum = rows.reduce((s, r) => s + r.price * (cart[r.key] || 0), 0);
  return (
    <section className="bf-sheet-sec" data-testid={`bf-sheet-cat-${id}`}>
      <header><h4>{title}</h4><span>{rows.length} {rows.length === 1 ? "produs" : "produse"}{sum > 0 ? ` · ales ${ron(sum)}` : ""}</span>
        {catalogs.map((k) => <a key={k.id} className="bf-sheet-catlink" href={catalogUrl(k.id)} target="_blank" rel="noopener noreferrer" data-testid={`bf-sheet-catalog-${id}-${k.id}`}><BookOpen size={13} /> {k.title}</a>)}
      </header>
      <div className="bf-sheet-wrap">
        <table className="bf-sheet">
          <thead><tr><th className="bf-sheet-img">Foto</th><th className="bf-sheet-code">Cod</th><th>Denumire produs</th><th className="bf-sheet-size">Gramaj</th><th>Preț BF</th><th>Buc.</th><th>Total</th></tr></thead>
          <tbody>{rows.slice(page * PAGE, page * PAGE + PAGE).map((r) => <Row key={r.key} r={r} q={cart[r.key] || 0} setQty={setQty} onZoom={onZoom} wait={wait} hasPage={!!pages[r.pid]} />)}</tbody>
        </table>
      </div>
      <Pager page={page} pages={pageCount} setPage={setPage} id={id} />
    </section>
  );
};

const Zoom = ({ r, close, page, catalogs }) => createPortal(
  <div className="bf-sheet-zoom" onClick={close} role="dialog" data-testid="bf-sheet-zoom">
    <figure onClick={(e) => e.stopPropagation()} className={page ? "has-page" : ""}>
      <button type="button" onClick={close} aria-label="Închide" data-testid="bf-sheet-zoom-close"><X size={18} /></button>
      {r.image && <img src={imgUrl(r.image)} alt={r.name} />}
      <figcaption><b>{r.code}</b> {r.name}{r.size ? ` · ${r.size}` : ""}</figcaption>
      {page && (
        <div className="bf-sheet-zoom-page" data-testid="bf-sheet-zoom-page">
          <small><BookOpen size={12} /> {catalogs.find((k) => k.id === page[0])?.title || "Catalog"} · pagina {page[1]}</small>
          <a href={`${catalogUrl(page[0])}#page=${page[1]}`} target="_blank" rel="noopener noreferrer"><img src={`/bf-catpages/${page[0]}-${page[1]}.jpg`} alt={`Pagina ${page[1]} din catalog`} loading="lazy" /></a>
          <a className="bf-sheet-zoom-open" href={`${catalogUrl(page[0])}#page=${page[1]}`} target="_blank" rel="noopener noreferrer" data-testid="bf-sheet-zoom-catalog">Deschide catalogul la pagina {page[1]}</a>
        </div>
      )}
    </figure>
  </div>,
  document.body,
);

const useWaits = () => {
  const [ids, setIds] = useState(() => new Set());
  const lead = localStorage.getItem("bf_lead_id");
  useEffect(() => { if (lead) fetchWaits(lead).then((l) => setIds(new Set(l))).catch(() => {}); }, [lead]);
  return {
    has: (pid) => ids.has(pid),
    add: (pid) => {
      if (!lead) return toast.error("Înscrie salonul ca să te putem anunța");
      addWait({ lead, pid, agent: localStorage.getItem("bf_agent") || "" })
        .then(() => { setIds((s) => new Set([...s, pid])); toast.success("Te anunțăm pe e-mail când produsul revine în stoc"); })
        .catch((e) => toast.error(e.response?.data?.detail || "Nu am putut salva cererea"));
    },
  };
};

export default function BFSheet({ rows, cart, setQty, q, catalogs = [] }) {
  const [zoom, setZoom] = useState(null);
  const [pages, setPages] = useState({});
  useEffect(() => { fetchCatalogPages().then(setPages).catch(() => {}); }, []);
  const wait = useWaits();
  useEffect(() => {
    if (!zoom) return undefined;
    const on = (e) => e.key === "Escape" && setZoom(null);
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [zoom]);
  const cats = [...PCATS, ["", "Alte produse"]].map(([k, t]) => [k, t, rows.filter((r) => r.cat === k || (!k && !PCATS.some(([x]) => x === r.cat)))]).filter(([, , l]) => l.length);
  return (
    <div className="bf-sheet-book" data-testid="bf-sheet">
      {cats.map(([k, t, l]) => <Section key={k || "alte"} id={k || "alte"} title={t} rows={l} cart={cart} setQty={setQty} q={q} onZoom={setZoom} wait={wait} catalogs={catalogs} pages={pages} />)}
      {zoom && <Zoom r={zoom} close={() => setZoom(null)} page={pages[zoom.pid]} catalogs={catalogs} />}
    </div>
  );
}
